import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Logo from './Logo'
import ArrowMark from './ArrowMark'

const PAGES = [
  { key: 'home',       label: 'Home',       path: '/' },
  { key: 'about',      label: 'About',      path: '/about' },
  { key: 'services',   label: 'Services',   path: '/services' },
  { key: 'projects',   label: 'Projects',   path: '/projects' },
  { key: 'highlights', label: 'Highlights', path: '/highlights' },
  { key: 'clients',    label: 'Clients',    path: '/clients' },
  { key: 'equipment',  label: 'Equipment',  path: '/equipment' },
]

export default function Nav() {
  const [scrolled, setScrolled]     = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Close menu on route change
  useEffect(() => { setMobileOpen(false) }, [pathname])

  // Lock body scroll while menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  return (
    <>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(20px) saturate(160%)',
        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        borderBottom: '1px solid var(--line-soft)',
        boxShadow: scrolled ? '0 4px 24px -8px rgba(0,0,0,0.06)' : 'none',
        transition: 'all .3s ease',
      }}>
        <div className="container" style={{
          display: 'flex', alignItems: 'center',
          height: scrolled ? 64 : 80,
          transition: 'height .3s ease', gap: 8,
        }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'var(--fg-0)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Logo size={scrolled ? 44 : 56} />
          </Link>
          <div style={{ flex: 1 }} />

          {/* Desktop nav links */}
          <ul style={{ display: 'flex', gap: 2, listStyle: 'none' }} className="nav-desktop">
            {PAGES.map(p => (
              <li key={p.key}>
                <Link to={p.path} style={{
                  display: 'inline-flex', alignItems: 'center', padding: '9px 13px',
                  fontFamily: 'var(--text)', fontSize: 15, fontWeight: 500,
                  color: pathname === p.path ? 'var(--accent)' : 'var(--fg-1)',
                  textDecoration: 'none', transition: 'color .2s, background .2s',
                  borderRadius: 999,
                  background: pathname === p.path ? 'var(--accent-soft)' : 'transparent',
                }}
                  onMouseEnter={e => { if (pathname !== p.path) e.currentTarget.style.color = 'var(--fg-0)' }}
                  onMouseLeave={e => { if (pathname !== p.path) e.currentTarget.style.color = 'var(--fg-1)' }}
                >{p.label}</Link>
              </li>
            ))}
          </ul>

          <Link to="/contact" className="btn btn-primary nav-desktop-only"
            style={{ padding: '10px 16px 10px 20px', marginLeft: 12, flexShrink: 0, fontSize: 14 }}>
            <span>Contact</span><span className="arrow-circle" style={{ width: 22, height: 22 }}><ArrowMark size={10} /></span>
          </Link>

          {/* Hamburger button */}
          <button
            onClick={() => setMobileOpen(true)}
            className="nav-mobile-only"
            aria-label="Open navigation menu"
            style={{
              background: 'transparent', border: '1px solid var(--line)',
              color: 'var(--fg-0)', width: 44, height: 44, cursor: 'pointer',
              borderRadius: 10, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 5,
              padding: 0, flexShrink: 0,
            }}
          >
            <span style={{ display: 'block', width: 18, height: 1.5, background: 'currentColor', borderRadius: 1 }} />
            <span style={{ display: 'block', width: 13, height: 1.5, background: 'currentColor', borderRadius: 1 }} />
            <span style={{ display: 'block', width: 18, height: 1.5, background: 'currentColor', borderRadius: 1 }} />
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay — full screen, scrollable */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(246,244,239,0.98)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}>
          {/* Top bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 20px 0', flexShrink: 0,
          }}>
            <Logo size={48} />
            <button
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation menu"
              style={{
                background: 'transparent', border: '1px solid var(--line)',
                color: 'var(--fg-0)', width: 44, height: 44, cursor: 'pointer',
                borderRadius: 10, display: 'flex', alignItems: 'center',
                justifyContent: 'center', padding: 0, flexShrink: 0,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Nav list */}
          <ul style={{
            listStyle: 'none',
            padding: '24px 20px',
            paddingBottom: 'calc(40px + env(safe-area-inset-bottom, 0px))',
            display: 'flex', flexDirection: 'column', flex: 1,
          }}>
            {PAGES.map((p, i) => (
              <li key={p.key} style={{ borderTop: '1px solid var(--line-soft)' }}>
                <Link to={p.path} style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  padding: '18px 4px',
                  fontFamily: 'var(--space)',
                  fontSize: 'clamp(20px, 6vw, 30px)',
                  color: pathname === p.path ? 'var(--accent)' : 'var(--fg-0)',
                  textDecoration: 'none', lineHeight: 1.2,
                }}>
                  <span style={{
                    fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--fg-3)',
                    minWidth: 20, flexShrink: 0,
                  }}>0{i + 1}</span>
                  <span style={{ flex: 1 }}>{p.label}</span>
                  {pathname === p.path && (
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                  )}
                </Link>
              </li>
            ))}

            {/* Contact CTA */}
            <li style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 24, marginTop: 4 }}>
              <Link to="/contact" style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                padding: '14px 22px 14px 26px',
                background: 'var(--accent)', color: '#fff',
                borderRadius: 999, textDecoration: 'none',
                fontFamily: 'var(--text)', fontSize: 15, fontWeight: 600,
              }}>
                Contact Us
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26, borderRadius: 5,
                  background: 'rgba(255,255,255,0.20)',
                }}><ArrowMark /></span>
              </Link>
            </li>
          </ul>
        </div>
      )}

      <style>{`
        .nav-desktop, .nav-desktop-only { display: flex !important; }
        .nav-mobile-only { display: none !important; }

        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-desktop-only { display: none !important; }
          .nav-mobile-only { display: flex !important; }
        }

        /* Tablet: show fewer items with tighter padding */
        @media (max-width: 1100px) and (min-width: 901px) {
          .nav-desktop a { padding: 8px 10px !important; font-size: 14px !important; }
        }
      `}</style>
    </>
  )
}
